

import { useContext, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { GlobalContext } from "../../Context/GlobalContext";

const cleanImageUrl = (value) => {
  const url = String(value || "").trim();

  if (!url) return "";

  // Defensive support in case a markdown-style URL ever reaches the UI.
  const markdownMatch = url.match(
    /^\[(https?:\/\/[^\]]+)\]\((https?:\/\/[^)]+)\)$/,
  );

  if (markdownMatch) {
    return markdownMatch[2] || markdownMatch[1] || "";
  }

  return url;
};

const normalizePackage = (item = {}) => ({
  package_id: String(item?.package_id ?? ""),
  catalogue_id: String(item?.catalogue_id ?? ""),
  parent_package_id:
    item?.parent_package_id === null ||
    item?.parent_package_id === undefined ||
    item?.parent_package_id === ""
      ? null
      : String(item.parent_package_id),
  package_name: String(item?.package_name ?? ""),
  package_type: String(item?.package_type ?? ""),
  description: String(item?.description ?? ""),
  pricing_method: String(item?.pricing_method ?? ""),
  minimum_persons: String(item?.minimum_persons ?? ""),
  maximum_persons:
    item?.maximum_persons === null || item?.maximum_persons === undefined
      ? null
      : String(item.maximum_persons),
  person_increment: String(item?.person_increment ?? ""),
  base_price:
    item?.base_price === null || item?.base_price === undefined
      ? null
      : String(item.base_price),
  display_order: String(item?.display_order ?? "0"),
  status: String(item?.status ?? ""),
  img_url: cleanImageUrl(item?.img_url),
});

const normalizePackageVariant = (item = {}) => ({
  package_variant_id: String(item?.package_variant_id ?? ""),
  package_id: String(item?.package_id ?? ""),
  variant_name: String(item?.variant_name ?? ""),
  minimum_persons:
    item?.minimum_persons === null ||
    item?.minimum_persons === undefined ||
    item?.minimum_persons === ""
      ? null
      : String(item.minimum_persons),
  maximum_persons:
    item?.maximum_persons === null ||
    item?.maximum_persons === undefined ||
    item?.maximum_persons === ""
      ? null
      : String(item.maximum_persons),
  exact_persons:
    item?.exact_persons === null ||
    item?.exact_persons === undefined ||
    item?.exact_persons === ""
      ? null
      : String(item.exact_persons),
  price:
    item?.price === null || item?.price === undefined
      ? "0"
      : String(item.price),
  status: String(item?.status ?? ""),
});

const sortPackageVariants = (a, b) => {
  const exactA = Number(a?.exact_persons ?? Number.MAX_SAFE_INTEGER);
  const exactB = Number(b?.exact_persons ?? Number.MAX_SAFE_INTEGER);

  if (Number.isFinite(exactA) && Number.isFinite(exactB) && exactA !== exactB) {
    return exactA - exactB;
  }

  const minA = Number(a?.minimum_persons ?? Number.MAX_SAFE_INTEGER);
  const minB = Number(b?.minimum_persons ?? Number.MAX_SAFE_INTEGER);

  if (Number.isFinite(minA) && Number.isFinite(minB) && minA !== minB) {
    return minA - minB;
  }

  return (
    Number(a?.package_variant_id || 0) - Number(b?.package_variant_id || 0)
  );
};

const normalizeItem = (item = {}) => ({
  item_id: item?.item_id ?? "",
  restaurant_id: item?.restaurant_id ?? "1",
  item_name: String(item?.item_name ?? ""),
  item_description: item?.item_description ?? null,
  item_type: String(item?.item_type ?? ""),
  image_url: cleanImageUrl(item?.image_url),
  tax_percentage: item?.tax_percentage ?? 0,
  addon_price: item?.addon_price ?? null,
  less_price: item?.less_price ?? null,
  upgrade_price: item?.upgrade_price ?? null,
  preparation_time_minutes:
    item?.preparation_time_minutes === null ||
    item?.preparation_time_minutes === undefined
      ? ""
      : String(item.preparation_time_minutes),
  status: String(item?.status ?? ""),
  created_at: item?.created_at ?? null,
  updated_at: item?.updated_at ?? null,
});

const sortPackages = (a, b) => {
  const orderA = Number(a?.display_order ?? 0);
  const orderB = Number(b?.display_order ?? 0);

  if (orderA !== orderB) return orderA - orderB;

  return Number(a?.package_id || 0) - Number(b?.package_id || 0);
};

export default function useCuisine() {
  const { api, anlookApi, userID } = useContext(GlobalContext);

  const packageFetchPromiseRef = useRef(null);
  const packageUpdatePromiseRef = useRef(null);
  const imageUploadPromiseRef = useRef(null);
  const itemMutationPromiseRef = useRef(null);
  const bhandareCatalogueIdRef = useRef("");

  const packageVariantFetchPromiseRef = useRef(new Map());
  const packageVariantMutationPromiseRef = useRef(null);

  // ================= BHANDARE PACKAGES =================

  const [bhandarePackages, setBhandarePackages] = useState([]);
  const [bhandarePackagesLoading, setBhandarePackagesLoading] = useState(false);
  const [bhandarePackageSaving, setBhandarePackageSaving] = useState(false);

  // Variants are stored package-wise:
  // { "38": [variant, variant, ...], "39": [...] }
  const [bhandarePackageVariants, setBhandarePackageVariants] = useState({});
  const [bhandarePackageVariantsLoading, setBhandarePackageVariantsLoading] =
    useState({});
  const [bhandarePackageVariantSaving, setBhandarePackageVariantSaving] =
    useState(false);
  const [
    bhandarePackageVariantDeletingId,
    setBhandarePackageVariantDeletingId,
  ] = useState(null);

  // ================= BHANDARE ITEMS =================

  const [bhandareItems, setBhandareItems] = useState([]);
  const [bhandareItemsLoading, setBhandareItemsLoading] = useState(false);
  const [bhandareItemSaving, setBhandareItemSaving] = useState(false);
  const [bhandareImageUploading, setBhandareImageUploading] = useState(false);

  // ================= PACKAGE TREE =================

  const bhandarePackageTree = useMemo(() => {
    const packageMap = new Map();
    const roots = [];

    bhandarePackages.forEach((pkg) => {
      packageMap.set(String(pkg.package_id), {
        ...pkg,
        children: [],
      });
    });

    packageMap.forEach((pkg) => {
      const parentId = pkg.parent_package_id
        ? String(pkg.parent_package_id)
        : null;

      if (parentId && packageMap.has(parentId)) {
        packageMap.get(parentId).children.push(pkg);
      } else {
        roots.push(pkg);
      }
    });

    const sortTree = (nodes) =>
      [...nodes].sort(sortPackages).map((node) => ({
        ...node,
        children: sortTree(node.children || []),
      }));

    return sortTree(roots);
  }, [bhandarePackages]);

  // =====================================================
  // GET BHANDARE WALA PACKAGES
  // =====================================================

  const fetchBhandareWalaPackages = async (catalogueId = "1") => {
    if (packageFetchPromiseRef.current) {
      return packageFetchPromiseRef.current;
    }

    const requestPromise = (async () => {
      try {
        if (!api) {
          toast.error("Main Anlook API instance missing");
          return [];
        }

        setBhandarePackagesLoading(true);

        const finalCatalogueId = String(catalogueId || "1");

        console.log(
          "GET BHANDARE PACKAGES:",
          "/Order/v1.0/getBhandarewalaPackages",
          { catalogueId: finalCatalogueId },
        );

        const res = await api.post("/Order/v1.0/getBhandarewalaPackages", {
          catalogueId: finalCatalogueId,
        });

        console.log("BHANDARE PACKAGES RESPONSE:", res?.data);

        if (res?.data?.responseCode === "00") {
          const responseData = res?.data?.response || res?.data?.data || [];

          const finalData = Array.isArray(responseData)
            ? responseData.map(normalizePackage).sort(sortPackages)
            : [];

          setBhandarePackages(finalData);

          return finalData;
        }

        setBhandarePackages([]);

        toast.error(
          res?.data?.responseMessage ||
            "Failed to fetch Bhandare Wala packages",
        );

        return [];
      } catch (err) {
        console.error(
          "Bhandare Wala packages fetch error:",
          err?.response?.data || err,
        );

        setBhandarePackages([]);

        toast.error(
          err?.response?.data?.responseMessage ||
            err?.response?.data?.message ||
            err?.message ||
            "Error fetching Bhandare Wala packages",
        );

        return [];
      } finally {
        setBhandarePackagesLoading(false);
      }
    })();

    packageFetchPromiseRef.current = requestPromise;

    try {
      return await requestPromise;
    } finally {
      // Only clear if this is still the current request.
      if (packageFetchPromiseRef.current === requestPromise) {
        packageFetchPromiseRef.current = null;
      }
    }
  };

  // =====================================================
  // UPDATE BHANDARE WALA PACKAGE
  // =====================================================

  const updateBhandareWalaPackage = async (payload) => {
    if (packageUpdatePromiseRef.current) {
      return packageUpdatePromiseRef.current;
    }

    const requestPromise = (async () => {
      try {
        setBhandarePackageSaving(true);

        const toNumber = (value, fallback = 0) => {
          if (value === "" || value === null || value === undefined) {
            return fallback;
          }

          const number = Number(value);
          return Number.isFinite(number) ? number : fallback;
        };

        const toNullableNumber = (value) => {
          if (value === "" || value === null || value === undefined) {
            return null;
          }

          const number = Number(value);
          return Number.isFinite(number) ? number : null;
        };

        const parentPackageId =
          payload?.parentPackageId === "" ||
          payload?.parentPackageId === null ||
          payload?.parentPackageId === undefined
            ? null
            : String(payload.parentPackageId);

        const requestPayload = {
          packageId: String(payload?.packageId ?? ""),
          catalogueId: String(payload?.catalogueId ?? "1"),
          parentPackageId,
          packageName: String(payload?.packageName ?? "").trim(),
          packageType: String(payload?.packageType ?? "").trim(),
          description: String(payload?.description ?? "").trim(),
          pricingMethod: String(payload?.pricingMethod ?? "").trim(),
          minimumPersons: toNumber(payload?.minimumPersons, 0),
          maximumPersons: toNullableNumber(payload?.maximumPersons),
          personIncrement: toNumber(payload?.personIncrement, 1),
          basePrice: toNullableNumber(payload?.basePrice),
          displayOrder: toNumber(payload?.displayOrder, 0),
          status: String(payload?.status ?? "ACTIVE").trim(),
          imgUrl: cleanImageUrl(payload?.imgUrl),
        };

        console.log("UPDATE BHANDARE PACKAGE PAYLOAD:", requestPayload);

        const res = await api.post(
          "/Order/v1.0/updateBhandarewalaPackages",
          requestPayload,
        );

        if (res?.data?.responseCode === "00") {
          toast.success(
            res?.data?.responseMessage || "Package updated successfully",
          );

          // Exactly one GET after exactly one successful UPDATE.
          await fetchBhandareWalaPackages(requestPayload.catalogueId);

          return true;
        }

        toast.error(res?.data?.responseMessage || "Failed to update package");

        return false;
      } catch (err) {
        console.error(
          "Bhandare Wala package update error:",
          err?.response?.data || err,
        );

        toast.error(
          err?.response?.data?.responseMessage ||
            err?.response?.data?.message ||
            err?.message ||
            "Error updating package",
        );

        return false;
      } finally {
        setBhandarePackageSaving(false);
      }
    })();

    packageUpdatePromiseRef.current = requestPromise;

    try {
      return await requestPromise;
    } finally {
      if (packageUpdatePromiseRef.current === requestPromise) {
        packageUpdatePromiseRef.current = null;
      }
    }
  };

  // =====================================================
  // GET BHANDARE WALA PACKAGE VARIANTS
  // =====================================================

  const fetchBhandareWalaPackageVariants = async (packageId) => {
    const finalPackageId = String(packageId ?? "").trim();

    if (!finalPackageId) return [];

    const existingPromise =
      packageVariantFetchPromiseRef.current.get(finalPackageId);

    if (existingPromise) {
      return existingPromise;
    }

    const requestPromise = (async () => {
      try {
        setBhandarePackageVariantsLoading((prev) => ({
          ...prev,
          [finalPackageId]: true,
        }));

        const res = await api.post(
          "/Order/v1.0/getBhandarewalaPackagesVariants",
          {
            packageId: finalPackageId,
          },
        );

        if (res?.data?.responseCode === "00") {
          const responseData = res?.data?.response || res?.data?.data || [];

          const finalData = Array.isArray(responseData)
            ? responseData
                .map(normalizePackageVariant)
                .sort(sortPackageVariants)
            : [];

          setBhandarePackageVariants((prev) => ({
            ...prev,
            [finalPackageId]: finalData,
          }));

          return finalData;
        }

        setBhandarePackageVariants((prev) => ({
          ...prev,
          [finalPackageId]: [],
        }));

        toast.error(
          res?.data?.responseMessage || "Failed to fetch package variants",
        );

        return [];
      } catch (err) {
        console.error(
          "Bhandare package variants fetch error:",
          err?.response?.data || err,
        );

        setBhandarePackageVariants((prev) => ({
          ...prev,
          [finalPackageId]: [],
        }));

        toast.error(
          err?.response?.data?.responseMessage ||
            err?.response?.data?.message ||
            err?.message ||
            "Error fetching package variants",
        );

        return [];
      } finally {
        setBhandarePackageVariantsLoading((prev) => ({
          ...prev,
          [finalPackageId]: false,
        }));
      }
    })();

    packageVariantFetchPromiseRef.current.set(finalPackageId, requestPromise);

    try {
      return await requestPromise;
    } finally {
      if (
        packageVariantFetchPromiseRef.current.get(finalPackageId) ===
        requestPromise
      ) {
        packageVariantFetchPromiseRef.current.delete(finalPackageId);
      }
    }
  };

  // =====================================================
  // INSERT PACKAGE VARIANT
  // =====================================================

  const insertBhandareWalaPackageVariant = async (payload) => {
    if (packageVariantMutationPromiseRef.current) {
      return packageVariantMutationPromiseRef.current;
    }

    const requestPromise = (async () => {
      try {
        setBhandarePackageVariantSaving(true);

        const toNullableNumber = (value) => {
          if (value === "" || value === null || value === undefined) {
            return null;
          }

          const number = Number(value);
          return Number.isFinite(number) ? number : null;
        };

        const toNumber = (value, fallback = 0) => {
          const number = Number(value);
          return Number.isFinite(number) ? number : fallback;
        };

        const requestPayload = {
          packageId: String(payload?.packageId ?? ""),
          variantName: String(payload?.variantName ?? "").trim(),
          minimumPersons: toNullableNumber(payload?.minimumPersons),
          maximumPersons: toNullableNumber(payload?.maximumPersons),
          exactPersons: toNullableNumber(payload?.exactPersons),
          price: toNumber(payload?.price, 0),
          status: String(payload?.status ?? "ACTIVE").trim(),
        };

        const res = await api.post(
          "/Order/v1.0/insertBhandarewalaPackagesVariants",
          requestPayload,
        );

        if (res?.data?.responseCode === "00") {
          toast.success(
            res?.data?.responseMessage || "Package variant added successfully",
          );

          await fetchBhandareWalaPackageVariants(requestPayload.packageId);

          return true;
        }

        toast.error(
          res?.data?.responseMessage || "Failed to add package variant",
        );

        return false;
      } catch (err) {
        console.error(
          "Insert package variant error:",
          err?.response?.data || err,
        );

        toast.error(
          err?.response?.data?.responseMessage ||
            err?.response?.data?.message ||
            err?.message ||
            "Error adding package variant",
        );

        return false;
      } finally {
        setBhandarePackageVariantSaving(false);
      }
    })();

    packageVariantMutationPromiseRef.current = requestPromise;

    try {
      return await requestPromise;
    } finally {
      if (packageVariantMutationPromiseRef.current === requestPromise) {
        packageVariantMutationPromiseRef.current = null;
      }
    }
  };

  // =====================================================
  // UPDATE PACKAGE VARIANT
  // =====================================================

  const updateBhandareWalaPackageVariant = async (payload) => {
    if (packageVariantMutationPromiseRef.current) {
      return packageVariantMutationPromiseRef.current;
    }

    const requestPromise = (async () => {
      try {
        setBhandarePackageVariantSaving(true);

        const toNullableNumber = (value) => {
          if (value === "" || value === null || value === undefined) {
            return null;
          }

          const number = Number(value);
          return Number.isFinite(number) ? number : null;
        };

        const toNumber = (value, fallback = 0) => {
          const number = Number(value);
          return Number.isFinite(number) ? number : fallback;
        };

        const requestPayload = {
          variantId: toNumber(
            payload?.variantId ?? payload?.package_variant_id,
            0,
          ),
          packageId: String(payload?.packageId ?? payload?.package_id ?? ""),
          variantName: String(payload?.variantName ?? "").trim(),
          minimumPersons: toNullableNumber(payload?.minimumPersons),
          maximumPersons: toNullableNumber(payload?.maximumPersons),
          exactPersons: toNullableNumber(payload?.exactPersons),
          price: toNumber(payload?.price, 0),
          status: String(payload?.status ?? "ACTIVE").trim(),
        };

        if (!requestPayload.variantId) {
          toast.error("Package variant ID is missing");
          return false;
        }

        if (!requestPayload.packageId) {
          toast.error("Package ID is missing");
          return false;
        }

        console.log("UPDATE PACKAGE VARIANT PAYLOAD:", requestPayload);

        const res = await api.post(
          "/Order/v1.0/updateBhandarewalaPackagesVariants",
          requestPayload,
        );

        if (res?.data?.responseCode === "00") {
          toast.success(
            res?.data?.responseMessage ||
              "Package variant updated successfully",
          );

          await fetchBhandareWalaPackageVariants(requestPayload.packageId);

          return true;
        }

        toast.error(
          res?.data?.responseMessage || "Failed to update package variant",
        );

        return false;
      } catch (err) {
        console.error(
          "Update package variant error:",
          err?.response?.data || err,
        );

        toast.error(
          err?.response?.data?.responseMessage ||
            err?.response?.data?.message ||
            err?.message ||
            "Error updating package variant",
        );

        return false;
      } finally {
        setBhandarePackageVariantSaving(false);
      }
    })();

    packageVariantMutationPromiseRef.current = requestPromise;

    try {
      return await requestPromise;
    } finally {
      if (packageVariantMutationPromiseRef.current === requestPromise) {
        packageVariantMutationPromiseRef.current = null;
      }
    }
  };

  // =====================================================
  // DELETE PACKAGE VARIANT
  // =====================================================

  const deleteBhandareWalaPackageVariant = async ({
    packageVariantId,
    packageId,
  }) => {
    if (packageVariantMutationPromiseRef.current) {
      return packageVariantMutationPromiseRef.current;
    }

    const finalVariantId = String(packageVariantId ?? "");
    const finalPackageId = String(packageId ?? "");

    const requestPromise = (async () => {
      try {
        setBhandarePackageVariantDeletingId(finalVariantId);

        const res = await api.post("/Order/deletePackageVariant", {
          packageVaraintId: finalVariantId,
        });

        if (res?.data?.responseCode === "00") {
          toast.success(
            res?.data?.responseMessage ||
              "Package variant deleted successfully",
          );

          await fetchBhandareWalaPackageVariants(finalPackageId);

          return true;
        }

        toast.error(
          res?.data?.responseMessage || "Failed to delete package variant",
        );

        return false;
      } catch (err) {
        console.error(
          "Delete package variant error:",
          err?.response?.data || err,
        );

        toast.error(
          err?.response?.data?.responseMessage ||
            err?.response?.data?.message ||
            err?.message ||
            "Error deleting package variant",
        );

        return false;
      } finally {
        setBhandarePackageVariantDeletingId(null);
      }
    })();

    packageVariantMutationPromiseRef.current = requestPromise;

    try {
      return await requestPromise;
    } finally {
      if (packageVariantMutationPromiseRef.current === requestPromise) {
        packageVariantMutationPromiseRef.current = null;
      }
    }
  };

  // =====================================================
  // RESOLVE BHANDARE CATALOG ID FROM USER CODE
  // =====================================================

  const resolveBhandareCatalogueId = async ({ force = false } = {}) => {
    const cachedId = String(bhandareCatalogueIdRef.current || "").trim();

    if (!force && cachedId) return cachedId;

    if (!api) {
      toast.error("Main Anlook API instance missing");
      return "";
    }

    const finalUserCode = String(userID || "").trim();

    if (!finalUserCode) {
      toast.error("User code is missing");
      return "";
    }

    console.log("GET CATALOG ID BY US CODE:", {
      endpoint: "/Inventory/getCatalogIdUSCode",
      payload: { usCode: finalUserCode },
    });

    const catalogueRes = await api.post("/Inventory/getCatalogIdUSCode", {
      usCode: finalUserCode,
    });

    console.log("CATALOG ID RESPONSE:", catalogueRes?.data);

    if (catalogueRes?.data?.responseCode !== "00") {
      toast.error(
        catalogueRes?.data?.responseMessage ||
          "Failed to fetch catalogue mapping ID",
      );
      return "";
    }

    const catalogueResponse = catalogueRes?.data?.response;
    const mappingId = Array.isArray(catalogueResponse)
      ? catalogueResponse?.[0]?.mapping_id
      : catalogueResponse?.mapping_id;
    const finalCatalogueId = String(mappingId ?? "").trim();

    if (!finalCatalogueId) {
      toast.error("Catalogue mapping ID not found");
      return "";
    }

    bhandareCatalogueIdRef.current = finalCatalogueId;
    return finalCatalogueId;
  };

  // =====================================================
  // GET BHANDARE WALA ITEMS
  // =====================================================

  const fetchBhandareWalaItems = async () => {
    try {
      setBhandareItemsLoading(true);

      const finalCatalogueId = await resolveBhandareCatalogueId();

      if (!finalCatalogueId) {
        setBhandareItems([]);
        return [];
      }

      // Use mapping_id as catalogId for the Bhandare Wala item list.
      console.log("GET ITEM LIST BW:", {
        endpoint: "/Order/getItemListBw",
        payload: { catalogId: finalCatalogueId },
      });

      const res = await api.post("/Order/getItemListBw", {
        catalogId: finalCatalogueId,
      });

      console.log("GET ITEM LIST BW RESPONSE:", res?.data);

      if (res?.data?.responseCode === "00") {
        const responseData = res?.data?.response || res?.data?.data || [];

        const finalData = Array.isArray(responseData)
          ? responseData.map(normalizeItem)
          : [];

        setBhandareItems(finalData);

        return finalData;
      }

      setBhandareItems([]);

      toast.error(
        res?.data?.responseMessage || "Failed to fetch Bhandare Wala items",
      );

      return [];
    } catch (err) {
      console.error(
        "Bhandare Wala items fetch error:",
        err?.response?.data || err,
      );

      setBhandareItems([]);

      toast.error(
        err?.response?.data?.responseMessage ||
          err?.response?.data?.message ||
          err?.message ||
          "Error fetching Bhandare Wala items",
      );

      return [];
    } finally {
      setBhandareItemsLoading(false);
    }
  };

  // =====================================================
  // IMAGE UPLOAD
  // =====================================================

  const uploadBhandareWalaImage = async ({
    item_name,
    image_data,
    type = "IMG",
  }) => {
    if (imageUploadPromiseRef.current) {
      return imageUploadPromiseRef.current;
    }

    const requestPromise = (async () => {
      try {
        if (!anlookApi) {
          toast.error("Anlook API instance missing");
          return null;
        }

        if (!image_data) {
          toast.error("Please select image");
          return null;
        }

        const finalImageData = String(image_data || "");
        const finalType = "IMG";

        if (!finalImageData.startsWith("data:image/")) {
          toast.error("Invalid image data");
          return null;
        }

        const dataSeparatorIndex = finalImageData.indexOf(",");

        if (dataSeparatorIndex === -1) {
          toast.error("Invalid image base64 data");
          return null;
        }

        const metadata = finalImageData.slice(0, dataSeparatorIndex);

        if (!metadata.includes(";base64")) {
          toast.error("Image must be base64 encoded");
          return null;
        }

        const base64 = finalImageData.slice(dataSeparatorIndex + 1);

        if (!base64) {
          toast.error("Image data is empty");
          return null;
        }

        const dataUrlMimeMatch = finalImageData.match(/^data:([^;,]+)[;,]/i);

        const dataUrlMime = String(dataUrlMimeMatch?.[1] || "").toLowerCase();

        setBhandareImageUploading(true);

        console.log("BHANDARE IMAGE UPLOAD:", {
          item_name,
          type: "IMG",
          mimeFromData: dataUrlMime,
          dataLength: finalImageData.length,
        });

        const res = await anlookApi.post("/v1/uploadItemImagev2.php", {
          userid: String(userID || "A1437"),
          item_name: String(item_name || `bhandare_item_${Date.now()}`),

          // Backend expects only IMG here.
          type: "IMG",

          // Original file encoded directly as Data URL.
          image_data: finalImageData,
        });

        if (String(res?.data?.status) === "0" && res?.data?.image_url) {
          return {
            ...res.data,
            image_url: cleanImageUrl(res.data.image_url),
          };
        }

        toast.error(
          res?.data?.message ||
            res?.data?.responseMessage ||
            "Image upload failed",
        );

        return null;
      } catch (err) {
        console.error(
          "Bhandare Wala image upload error:",
          err?.response?.data || err,
        );

        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.responseMessage ||
            err?.message ||
            "Image upload failed",
        );

        return null;
      } finally {
        setBhandareImageUploading(false);
      }
    })();

    imageUploadPromiseRef.current = requestPromise;

    try {
      return await requestPromise;
    } finally {
      if (imageUploadPromiseRef.current === requestPromise) {
        imageUploadPromiseRef.current = null;
      }
    }
  };

  // =====================================================
  // INSERT BHANDARE WALA ITEM
  // =====================================================

  const insertBhandareWalaItem = async (payload) => {
    if (itemMutationPromiseRef.current) {
      return itemMutationPromiseRef.current;
    }

    const requestPromise = (async () => {
      try {
        setBhandareItemSaving(true);

        const requestPayload = {
          restaurant_id: String(payload?.restaurant_id ?? "1"),
          item_name: String(payload?.item_name ?? "").trim(),
          item_description: String(payload?.item_description ?? "").trim(),
          item_type: String(payload?.item_type ?? "VEG").trim(),
          image_url: cleanImageUrl(payload?.image_url),
          tax_percentage: String(payload?.tax_percentage ?? "0").trim(),
          preparation_time_minutes: String(
            payload?.preparation_time_minutes ?? "0",
          ).trim(),
        };

        console.log("INSERT BHANDARE ITEM PAYLOAD:", requestPayload);

        const res = await api.post(
          "/Order/insertItemsBhandareWala",
          requestPayload,
        );

        if (res?.data?.responseCode === "00") {
          toast.success(
            res?.data?.responseMessage || "Item added successfully",
          );

          await fetchBhandareWalaItems();
          return true;
        }

        toast.error(res?.data?.responseMessage || "Failed to add item");
        return false;
      } catch (err) {
        console.error(
          "Bhandare Wala item insert error:",
          err?.response?.data || err,
        );

        toast.error(
          err?.response?.data?.responseMessage ||
            err?.response?.data?.message ||
            err?.message ||
            "Error adding item",
        );

        return false;
      } finally {
        setBhandareItemSaving(false);
      }
    })();

    itemMutationPromiseRef.current = requestPromise;

    try {
      return await requestPromise;
    } finally {
      if (itemMutationPromiseRef.current === requestPromise) {
        itemMutationPromiseRef.current = null;
      }
    }
  };

  // =====================================================
  // UPDATE BHANDARE WALA ITEM
  // =====================================================

  const updateBhandareWalaItem = async (payload) => {
    if (itemMutationPromiseRef.current) {
      return itemMutationPromiseRef.current;
    }

    const requestPromise = (async () => {
      try {
        setBhandareItemSaving(true);

        const finalCatalogueId = await resolveBhandareCatalogueId();

        if (!finalCatalogueId) return false;

        const toNumber = (value, fallback = 0) => {
          const number = Number(value);
          return Number.isFinite(number) ? number : fallback;
        };

        const requestPayload = {
          catalogId: finalCatalogueId,
          itemId: String(payload?.itemId ?? payload?.item_id ?? "").trim(),
          item_name: String(payload?.item_name ?? "").trim(),
          item_description: String(payload?.item_description ?? "").trim(),
          item_type: String(payload?.item_type ?? "VEG").trim(),
          image_url: cleanImageUrl(payload?.image_url ?? payload?.item_url),
          tax_percentage: toNumber(payload?.tax_percentage, 0),
          addon_price: String(payload?.addon_price ?? "").trim(),
          less_price: String(payload?.less_price ?? "").trim(),
          upgrade_price: String(payload?.upgrade_price ?? "").trim(),
          preparation_time_minutes: toNumber(
            payload?.preparation_time_minutes,
            0,
          ),
          status: String(payload?.status ?? "ACTIVE").trim(),
        };

        if (!requestPayload.itemId) {
          toast.error("Item ID is missing");
          return false;
        }

        console.log("UPDATE BHANDARE ITEM PAYLOAD:", requestPayload);

        const itemUpdateRes = await api.post(
          "/Order/updateItemsBhandarewala",
          requestPayload,
        );

        if (itemUpdateRes?.data?.responseCode !== "00") {
          toast.error(
            itemUpdateRes?.data?.responseMessage ||
              "Failed to update Bhandare Wala item",
          );
          return false;
        }

        console.log("UPDATE BHANDARE MENU ITEM PAYLOAD:", requestPayload);

        const menuUpdateRes = await api.post(
          "/Order/updateMenuItemsBhandarewala",
          requestPayload,
        );

        if (menuUpdateRes?.data?.responseCode !== "00") {
          toast.error(
            menuUpdateRes?.data?.responseMessage ||
              "Item updated, but menu item update failed",
          );
          return false;
        }

        toast.success(
          menuUpdateRes?.data?.responseMessage ||
            itemUpdateRes?.data?.responseMessage ||
            "Item and menu item updated successfully",
        );

        await fetchBhandareWalaItems();
        return true;
      } catch (err) {
        console.error(
          "Bhandare Wala item update error:",
          err?.response?.data || err,
        );

        toast.error(
          err?.response?.data?.responseMessage ||
            err?.response?.data?.message ||
            err?.message ||
            "Error updating item",
        );

        return false;
      } finally {
        setBhandareItemSaving(false);
      }
    })();

    itemMutationPromiseRef.current = requestPromise;

    try {
      return await requestPromise;
    } finally {
      if (itemMutationPromiseRef.current === requestPromise) {
        itemMutationPromiseRef.current = null;
      }
    }
  };

  return {
    // USER
    userID,

    // PACKAGES
    bhandarePackages,
    bhandarePackagesLoading,
    bhandarePackageSaving,
    bhandarePackageTree,
    fetchBhandareWalaPackages,
    updateBhandareWalaPackage,

    // PACKAGE VARIANTS
    bhandarePackageVariants,
    bhandarePackageVariantsLoading,
    bhandarePackageVariantSaving,
    bhandarePackageVariantDeletingId,
    fetchBhandareWalaPackageVariants,
    insertBhandareWalaPackageVariant,
    updateBhandareWalaPackageVariant,
    deleteBhandareWalaPackageVariant,

    // ITEMS
    bhandareItems,
    bhandareItemsLoading,
    fetchBhandareWalaItems,

    // EDIT / IMAGE
    bhandareItemSaving,
    bhandareImageUploading,
    uploadBhandareWalaImage,
    insertBhandareWalaItem,
    updateBhandareWalaItem,
  };
}